import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    Box,
    Typography,
    Alert,
    Skeleton,
} from '@mui/material';
import { getProjectDashboard } from '../services/api';

function ProjectLandingPage() {
    const { projectId } = useParams();
    const navigate = useNavigate();
    const [error, setError] = useState(null);

    useEffect(() => {
        let isActive = true;

        const resolveLandingRoute = async () => {
            try {
                const dashboardData = await getProjectDashboard(projectId);
                if (!isActive) return;

                const mappingSummaries = dashboardData?.mapping_summaries || {};
                const hasMappingOutput = Object.values(mappingSummaries).some(
                    (summary) => Boolean(summary?.processed)
                );
                let preferredFile = null;

                if (hasMappingOutput) {
                    for (const sourceSystem of ['DFSS', 'IDW']) {
                        const files = dashboardData?.files?.[sourceSystem] || [];
                        const matchedFile = files.find((file) => Boolean(mappingSummaries[file.id]?.processed));
                        if (matchedFile) {
                            preferredFile = { fileId: matchedFile.id, sourceSystem };
                            break;
                        }
                    }
                }

                navigate(
                    hasMappingOutput
                        ? `/schema-analyzer/projects/${projectId}/mapper`
                        : `/schema-analyzer/projects/${projectId}/upload`,
                    {
                        replace: true,
                        state: preferredFile ? { preferredFile } : null,
                    }
                );
            } catch (err) {
                if (!isActive) return;

                if (err.response?.status === 404) {
                    setError('Project not found.');
                } else {
                    setError('Failed to open project. Please ensure the backend is running.');
                }
                console.error('Error resolving project landing page:', err);
            }
        };

        resolveLandingRoute();

        return () => {
            isActive = false;
        };
    }, [navigate, projectId]);

    return (
        <Box>
            {error ? (
                <Alert severity="error">{error}</Alert>
            ) : (
                <>
                    <Skeleton variant="text" width={220} height={42} />
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Loading project...
                    </Typography>
                    <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
                </>
            )}
        </Box>
    );
}

export default ProjectLandingPage;
